import React from 'react'
import PropTypes from 'prop-types'
import {
  bindAll,
  camelCase,
  get,
  omit,
  has,
  isArray,
  isFunction,
  isUndefined,
  tail,
  transform,
  zipObject,
} from 'lodash-es'

// importing from shared/components doesn't work here, may be to the order the imports are evaluated
import DynamicModuleLoaderComponent from '../../components/DynamicModuleLoaderComponent/DynamicModuleLoaderComponent'
import { yieldSagasGenerator } from '..'

import FeatureWrapperComponent from './FeatureWrapperComponent'

const generateActions = (namespace, actions) => {
  const namespaceUpper = namespace.toUpperCase()
  const getActionName = (config) => (isArray(config) ? config[0] : config)
  const getActionParams = (config, params) => {
    if (isArray(config)) {
      return zipObject(tail(config), params)
    }

    return { payload: params }
  }

  const actionNames = transform(
    actions,
    (acc, actionConfig) => {
      const actionName = getActionName(actionConfig)

      acc[actionName] = `${namespaceUpper}:${actionName}`
      return acc
    },
    {}
  )

  const actionCreators = transform(
    actions,
    (acc, actionConfig) => {
      const actionName = getActionName(actionConfig)
      acc[camelCase(actionName)] = (...rest) => ({
        type: actionNames[actionName],
        ...getActionParams(actionConfig, rest),
      })
      return acc
    },
    {}
  )

  return {
    actionNames,
    ...actionCreators,
  }
}

const getDisplayName = (WrappedComponent = {}) => {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component'
}

const getFeatureRootState = (state, namespace) =>
  get(state, `feature-${namespace}`, {})

const bundleFeature = (bundle) => {
  const { Component, reducer, actions, sagas, selectors } = bundle

  class BundleFeatureClass extends React.Component {
    constructor(props) {
      super(props)

      this.namespace = props.namespace

      if (actions) {
        const namespaceActions = generateActions(this.namespace, actions)
        this.actionNames = get(namespaceActions, 'actionNames')
        this.actionCreators = omit(namespaceActions, 'actionNames')
      }

      bindAll(this, 'getFeatureRoot')

      // getFeatureRoot is used in the selectors so we need to bind it before that
      this.selectors = this.getSelectors(props)
    }

    getFeatureRoot(state) {
      return getFeatureRootState(state, this.namespace)
    }

    getFeatureModule() {
      const { featureProps, ...props } = this.props
      // oxlint-disable-next-line typescript/unbound-method
      const { getFeatureRoot, actionNames, namespace } = this

      let reducerMap
      let sagasFeature

      if (isFunction(reducer)) {
        reducerMap = {
          [`feature-${namespace}`]: reducer({ actionNames, ...this.props }),
        }
      }

      if (!isUndefined(sagas)) {
        sagasFeature = yieldSagasGenerator(
          sagas({
            actionNames,
            options: featureProps, // legacy to be removed when featureProps is not used anymore
            props,
            getFeatureRoot,
            selectors: this.selectors,
          }).default
        )
      }

      return {
        id: namespace,
        ...(reducerMap ? { reducerMap } : {}),
        ...(sagasFeature ? { sagas: [sagasFeature] } : {}),
        retained: !has(actionNames, 'RESET_STORE_DATA'),
      }
    }

    getSelectors(props) {
      return selectors
        ? // oxlint-disable-next-line typescript/unbound-method
          selectors({ props, getFeatureRoot: this.getFeatureRoot })
        : undefined
    }

    render() {
      return (
        <DynamicModuleLoaderComponent modules={[this.getFeatureModule()]}>
          <FeatureWrapperComponent
            {...this.props}
            wrappedComponent={Component}
            actionCreators={this.actionCreators}
            selectors={this.selectors}
          />
        </DynamicModuleLoaderComponent>
      )
    }
  }

  BundleFeatureClass.displayName = `BundleFeature(${getDisplayName(Component)})`

  BundleFeatureClass.propTypes = {
    namespace: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types
    featureProps: PropTypes.object,
  }

  BundleFeatureClass.defaultProps = {
    featureProps: {},
  }

  return BundleFeatureClass
}

export const namespaceSelectors = (selectors, namespace) => {
  return selectors({
    getFeatureRoot: (state) => getFeatureRootState(state, namespace),
  })
}

// oxlint-disable-next-line import/no-default-export
export default bundleFeature
